import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowRight, QrCode, ChevronDown } from "lucide-react";
import { api, API, withToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CarQrButton } from "../components/CarQrButton";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const empty = { plate: "", brand: "", model: "", year: "", color: "", driver_name: "", image_url: "", value: "", notes: "", circulation_card_expiry: "" };

export default function Cars() {
    const { isAdmin, canWrite } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = async () => {
        const { data } = await api.get("/cars");
        setItems(data);
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (c) => {
        setEditing(c);
        setForm({
            plate: c.plate, brand: c.brand, model: c.model,
            year: c.year || "", color: c.color || "", driver_name: c.driver_name || "",
            image_url: c.image_url || "", value: c.value ?? "", notes: c.notes || "",
            circulation_card_expiry: c.circulation_card_expiry || "",
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.plate || !form.brand || !form.model) {
            toast.error("Placa, marca y modelo son obligatorios");
            return;
        }
        const payload = {
            ...form,
            year: form.year ? Number(form.year) : null,
            value: form.value !== "" ? Number(form.value) : null,
        };
        try {
            if (editing) {
                await api.put(`/cars/${editing.id}`, payload);
                toast.success("Auto actualizado");
            } else {
                await api.post("/cars", payload);
                toast.success("Auto agregado");
            }
            setOpen(false);
            load();
        } catch {
            toast.error("Error al guardar");
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`¿Eliminar ${c.plate}? Los movimientos asociados quedarán huérfanos.`)) return;
        await api.delete(`/cars/${c.id}`);
        toast.success("Eliminado");
        load();
    };

    return (
        <div className="space-y-6" data-testid="cars-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Flotilla</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">Autos</h1>
                    <p className="text-sm text-slate-500 mt-1">Administra tus vehículos y entra al detalle de cada uno.</p>
                </div>
                {canWrite && (
                    <div className="flex gap-2">
                        {items.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="gap-2" data-testid="download-fleet-qr-menu-trigger">
                                        <QrCode size={16} /> Descargar QR de la flotilla <ChevronDown size={14} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem asChild>
                                        <a
                                            href={withToken(`${API}/cars/qr-batch/pdf?base_url=${encodeURIComponent(window.location.origin)}`)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="cursor-pointer"
                                            data-testid="download-fleet-qr-button"
                                        >
                                            QR grandes (varias páginas)
                                        </a>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <a
                                            href={withToken(`${API}/cars/qr-batch-single-page/pdf?base_url=${encodeURIComponent(window.location.origin)}`)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="cursor-pointer"
                                            data-testid="download-fleet-qr-single-page-button"
                                        >
                                            Todos en 1 sola página
                                        </a>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-car-button">
                            <Plus size={16} className="mr-1" /> Nuevo auto
                        </Button>
                    </div>
                )}
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.length === 0 && (
                    <div className="col-span-full text-center py-16 text-slate-400">Aún no hay autos. Agrega el primero.</div>
                )}
                {items.map((c) => (
                    <Card key={c.id} className="border-slate-200 shadow-sm overflow-hidden" data-testid={`car-${c.id}`}>
                        {c.image_url ? (
                            <div className="h-36 bg-slate-100 overflow-hidden">
                                <img src={c.image_url} alt={c.plate} className="w-full h-full object-cover" />
                            </div>
                        ) : (
                            <div className="h-36 bg-slate-100 grid place-items-center text-slate-300 font-display text-3xl">
                                {c.plate.slice(0, 3)}
                            </div>
                        )}
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-slate-500">{c.plate}</div>
                                    <div className="font-display font-medium text-slate-900 mt-1">{c.brand} {c.model}</div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        {[c.year, c.color, c.driver_name].filter(Boolean).join(" · ")}
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <CarQrButton car={c} />
                                    {canWrite && (
                                        <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`edit-car-${c.id}`}>
                                            <Pencil size={14} />
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button onClick={() => remove(c)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" data-testid={`delete-car-${c.id}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <Link to={`/autos/${c.id}`} className="mt-4 inline-flex items-center text-sm font-medium text-slate-900 hover:underline" data-testid={`detail-car-${c.id}`}>
                                Ver detalle <ArrowRight size={14} className="ml-1" />
                            </Link>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="car-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar auto" : "Nuevo auto"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Placa</Label>
                            <Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="car-plate" />
                        </div>
                        <div>
                            <Label className="text-xs">Año</Label>
                            <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Marca</Label>
                            <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="car-brand" />
                        </div>
                        <div>
                            <Label className="text-xs">Modelo</Label>
                            <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="car-model" />
                        </div>
                        <div>
                            <Label className="text-xs">Color</Label>
                            <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Conductor</Label>
                            <Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Valor del auto (MXN)</Label>
                            <Input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="car-value" placeholder="Ej. 285000.00" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Vencimiento tarjeta de circulación</Label>
                            <Input type="date" value={form.circulation_card_expiry} onChange={(e) => setForm({ ...form, circulation_card_expiry: e.target.value })} data-testid="car-circulation-card-expiry" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">URL de imagen</Label>
                            <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Notas</Label>
                            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-car">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
